import React, { useState } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { usePermissions } from '../hooks/usePermissions';
import { updateNotificationConfig, testNotificationChannel } from '../services/tenantService';
import {
  Save,
  CheckCircle,
  XCircle,
  Loader,
  Send,
  MessageSquare,
  Hash,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';

const CHANNELS = [
  {
    key: 'slack',
    label: 'Slack',
    icon: Hash,
    color: 'text-[#4A154B]',
    fields: [
      {
        name: 'slackWebhookUrl',
        label: 'Webhook URL',
        type: 'password',
        placeholder: 'https://hooks.slack.com/services/...',
        secretFlag: 'hasSlackWebhook',
        configKey: 'notifications.slackWebhookUrl',
      },
    ],
    enabledKey: 'slackEnabled',
    configEnabledKey: 'notifications.slackEnabled',
    helpUrl: 'https://api.slack.com/messaging/webhooks',
    helpText: 'Create an Incoming Webhook in your Slack workspace.',
  },
  {
    key: 'discord',
    label: 'Discord',
    icon: MessageSquare,
    color: 'text-[#5865F2]',
    fields: [
      {
        name: 'discordWebhookUrl',
        label: 'Webhook URL',
        type: 'password',
        placeholder: 'https://discord.com/api/webhooks/...',
        secretFlag: 'hasDiscordWebhook',
        configKey: 'notifications.discordWebhookUrl',
      },
    ],
    enabledKey: 'discordEnabled',
    configEnabledKey: 'notifications.discordEnabled',
    helpUrl: 'https://support.discord.com/hc/en-us/articles/228383668',
    helpText: 'Create a Webhook in your Discord channel settings.',
  },
  {
    key: 'telegram',
    label: 'Telegram',
    icon: Send,
    color: 'text-[#0088cc]',
    fields: [
      {
        name: 'telegramBotToken',
        label: 'Bot Token',
        type: 'password',
        placeholder: '123456:ABC-DEF...',
        secretFlag: 'hasTelegramToken',
        configKey: 'notifications.telegramBotToken',
      },
      {
        name: 'telegramChatId',
        label: 'Chat ID',
        type: 'text',
        placeholder: '-1001234567890',
        configKey: 'notifications.telegramChatId',
      },
    ],
    enabledKey: 'telegramEnabled',
    configEnabledKey: 'notifications.telegramEnabled',
    helpUrl: 'https://core.telegram.org/bots#how-do-i-create-a-bot',
    helpText: 'Create a bot via @BotFather and get your chat ID.',
  },
];

export function NotificationChannels() {
  const { tenantConfig, refreshConfig } = useTenant();
  const { canUpdateTenantConfig } = usePermissions();
  const canEdit = canUpdateTenantConfig();
  const notifications = tenantConfig?.notifications || {};

  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(null);
  const [testing, setTesting] = useState(null);
  const [status, setStatus] = useState({});

  const getFieldValue = (field) => {
    if (formData[field.name] !== undefined) return formData[field.name];
    if (field.type === 'password') return '';
    return notifications[field.name] || '';
  };

  const hasFieldChanged = (field) => {
    return formData[field.name] !== undefined && formData[field.name] !== '';
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleToggle = async (channel) => {
    const currentEnabled = notifications[channel.enabledKey] || false;
    setSaving(channel.key);
    try {
      await updateNotificationConfig({ [channel.configEnabledKey]: !currentEnabled });
      await refreshConfig();
      setStatus({ [channel.key]: 'success' });
    } catch (err) {
      setStatus({ [channel.key]: 'error' });
    }
    setTimeout(() => setStatus({}), 2000);
    setSaving(null);
  };

  const handleSave = async (channel) => {
    const updates = {};
    channel.fields.forEach(field => {
      if (hasFieldChanged(field)) {
        updates[field.configKey] = formData[field.name];
      }
    });

    if (Object.keys(updates).length === 0) return;

    setSaving(channel.key);
    try {
      await updateNotificationConfig(updates);
      await refreshConfig();
      setFormData(prev => {
        const next = { ...prev };
        channel.fields.forEach(f => delete next[f.name]);
        return next;
      });
      setStatus({ [channel.key]: 'success' });
    } catch (err) {
      setStatus({ [channel.key]: 'error' });
    }
    setTimeout(() => setStatus({}), 2000);
    setSaving(null);
  };

  const handleTest = async (channel) => {
    setTesting(channel.key);
    try {
      await testNotificationChannel(channel.key);
      setStatus({ [channel.key]: 'test-success' });
    } catch (err) {
      setStatus({ [channel.key]: 'test-error' });
      alert(`Test failed: ${err.message}`);
    }
    setTimeout(() => setStatus({}), 3000);
    setTesting(null);
  };

  const isChannelConfigured = (channel) => {
    return channel.fields.every(f => {
      if (f.secretFlag) return notifications[f.secretFlag];
      return !!notifications[f.name];
    });
  };

  const hasUnsavedChanges = (channel) => {
    return channel.fields.some(f => hasFieldChanged(f));
  };

  return (
    <div className="space-y-4">
      {CHANNELS.map(channel => {
        const Icon = channel.icon;
        const enabled = notifications[channel.enabledKey] || false;
        const configured = isChannelConfigured(channel);

        return (
          <div key={channel.key} className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icon className={`w-5 h-5 ${channel.color}`} />
                <span className="font-medium text-gray-900">{channel.label}</span>
                {configured && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                    Configured
                  </span>
                )}
              </div>
              <button
                onClick={() => handleToggle(channel)}
                disabled={saving === channel.key || !canEdit}
                className="flex items-center gap-1 text-sm"
              >
                {enabled ? (
                  <ToggleRight className="w-8 h-8 text-primary-600" />
                ) : (
                  <ToggleLeft className="w-8 h-8 text-gray-400" />
                )}
              </button>
            </div>

            <p className="text-xs text-gray-500 mb-3">
              {channel.helpText}{' '}
              <a href={channel.helpUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                Learn more
              </a>
            </p>

            <div className="space-y-3">
              {channel.fields.map(field => (
                <div key={field.name}>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {field.label}
                  </label>
                  <input
                    type={field.type}
                    value={getFieldValue(field)}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    disabled={!canEdit}
                    placeholder={field.secretFlag && notifications[field.secretFlag] && !hasFieldChanged(field)
                      ? '••••••••'
                      : field.placeholder}
                  />
                </div>
              ))}
            </div>

            <div className="flex gap-2 mt-3">
              <button
                onClick={() => handleSave(channel)}
                disabled={!hasUnsavedChanges(channel) || saving === channel.key || !canEdit}
                className="px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 text-sm font-medium transition-colors"
              >
                {saving === channel.key ? (
                  <Loader className="w-3.5 h-3.5 animate-spin" />
                ) : status[channel.key] === 'success' ? (
                  <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                ) : status[channel.key] === 'error' ? (
                  <XCircle className="w-3.5 h-3.5 text-red-400" />
                ) : (
                  <Save className="w-3.5 h-3.5" />
                )}
                Save
              </button>
              <button
                onClick={() => handleTest(channel)}
                disabled={!configured || testing === channel.key || !canEdit}
                className="px-3 py-1.5 bg-white text-primary-700 border border-primary-200 rounded-md hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 text-sm font-medium transition-colors"
              >
                {testing === channel.key ? (
                  <Loader className="w-3.5 h-3.5 animate-spin" />
                ) : status[channel.key] === 'test-success' ? (
                  <CheckCircle className="w-3.5 h-3.5" />
                ) : (
                  <Send className="w-3.5 h-3.5" />
                )}
                Test
              </button>
            </div>
          </div>
        );
      })}

    </div>
  );
}
